import React, { SetStateAction, useEffect, useMemo, useRef, useState } from 'react';

import { type Control, Controller, type FieldErrors } from 'react-hook-form';
import TCClient from '../../../../client';
import { ScimUser, User, UserData, UserInfo, UserSettings } from '../../../types';

import { SettingText } from '../../../shared-components/SettingText/SettingText';
import { Switch, Case } from '../../../shared-components/Switch';
import { Input } from '../../../shared-components/Input/Input';
import { Checkbox } from '../../../shared-components/Checkbox/Checkbox';
import avatarNoIconBG from '../../../images/test_avatar_noicon_bg.png';
import { ReactComponent as XIcon } from '../../../../common/images/x-gray-icon.svg';

import styles from './index.module.css';
import { LocationsList } from './LocationsList';
import { type FormFields } from '.';
import { DotsIndicator } from 'common/components';
import canEditUserEmailAddress from 'admin/utils/canEditUserEmailAddress';

import { useAppSelector } from 'redux-stores';

export type UserInfoFormErrors = {
  displayName?: string[];
  title?: string[];
  department?: string[];
};

const formatUserProperty = (property: string) => {
  return property
    .split('|')
    .map((item) => item.trim())
    .join(', ');
};

type UserInfoFormProps = {
  isEditableEmailAddressEnabled: boolean;
  control: Control<FormFields>;
  errors: FieldErrors<UserInfo> | undefined;
  isProductSettingsAdmin: boolean;
  isPersonaBasedManagementAllowed: boolean;
  isScimSettingEnabled: boolean;
  selectedUser: User;
  setUnlinkPhoneNumber: React.Dispatch<SetStateAction<string>>;
  setUserInfo: (userInfo: UserInfo) => void;
  userInfo: UserInfo;
  userSettings?: UserSettings;
  setUserSettings: (userSettings: UserSettings) => void;
  userData: UserData;
  isCustomAttributesFeatureFlagEnabled: boolean;
  isLocationsEnabled: boolean;
};

export default function UserInfoForm({
  errors,
  isEditableEmailAddressEnabled,
  isCustomAttributesFeatureFlagEnabled,
  isProductSettingsAdmin,
  isPersonaBasedManagementAllowed,
  isScimSettingEnabled,
  setUnlinkPhoneNumber,
  selectedUser,
  setUserInfo,
  setUserSettings,
  userInfo,
  userData,
  userSettings,
  control,
  isLocationsEnabled,
}: UserInfoFormProps) {
  const organizationId = useAppSelector((state) => state.admin.selectedOrganizationId);
  const formTopRef = useRef<HTMLDivElement>(null);
  const canEditEmailAddress = canEditUserEmailAddress({
    organizationSettings: userData.organizationSettings,
    currentOrganizationId: organizationId,
    isEditableEmailAddressEnabled,
  });

  return (
    <div ref={formTopRef} className={styles.userSettings}>
      <div className={styles.userInfo}>
        <div className={styles.avatar}>
          <img
            alt={'avatar'}
            src={selectedUser?.avatarUrl || avatarNoIconBG}
            width={140}
            height={140}
          />
        </div>
        <div>
          <div className={styles.field}>
            <SettingText
              label={'Display Name'}
              size={'large'}
              tagType="label"
              htmlFor="displayName"
            />
            <div className={styles.inputContainer}>
              <Controller
                name="displayName"
                control={control}
                rules={{ required: 'Display Name is required.' }}
                render={({ field: { onChange } }) => (
                  <Input
                    customCSS={errors?.displayName ? styles.inputError : ''}
                    id="displayName"
                    value={userInfo.displayName}
                    onInputChange={(e) => {
                      setUserInfo({ ...userInfo, displayName: e.target.value });
                      onChange(e.target.value);
                    }}
                  />
                )}
              />
              {errors?.displayName && (
                <div className={styles.error}>{errors.displayName.message}</div>
              )}
            </div>
          </div>
          <div className={styles.field}>
            <SettingText label={'Title'} size={'large'} />
            <Input
              value={userInfo.title}
              onInputChange={(e) => {
                setUserInfo({ ...userInfo, title: e.target.value });
              }}
            />
          </div>
          <div className={styles.field}>
            <SettingText label={'Department'} size={'large'} />
            <Input
              value={userInfo.department}
              onInputChange={(e) => {
                setUserInfo({ ...userInfo, department: e.target.value });
              }}
            />
          </div>
          {isCustomAttributesFeatureFlagEnabled && (
            <>
              <div className={styles.field}>
                <SettingText label={'Location'} size={'large'} />
                <div className={styles.settingReadInfo}>
                  {userData.location?.length
                    ? formatUserProperty(userData.location)
                    : 'No Location'}
                </div>
              </div>
              <div className={styles.field}>
                <SettingText label={'Insurance'} size={'large'} />
                <div className={styles.settingReadInfo}>
                  {userData.insurance?.length
                    ? formatUserProperty(userData.insurance)
                    : 'No Insurance'}
                </div>
              </div>
              <div className={styles.field}>
                <SettingText label={'Specialty'} size={'large'} />
                <div className={styles.settingReadInfo}>
                  {userData.specialty?.length
                    ? formatUserProperty(userData.specialty)
                    : 'No Specialty'}
                </div>
              </div>
            </>
          )}
          {userData.username !== null && (
            <div className={styles.field}>
              <SettingText label={'Username'} size={'large'} />
              <div className={styles.settingReadInfo}>{userData.username}</div>
            </div>
          )}
          <Switch value={canEditEmailAddress}>
            <Case when={false}>
              <div className={styles.field}>
                <SettingText label={'Email Address'} size={'large'} />
                <div className={styles.settingReadInfo}>
                  {userData?.emailAddresses[0]?.emailAddress}
                </div>
              </div>
            </Case>
            <Case when={true}>
              <div className={styles.field}>
                <SettingText label={'Email Address'} size={'large'} />
                <div className={styles.inputContainer}>
                  <Controller
                    name="emailAddress"
                    control={control}
                    rules={{
                      required: 'Email Address is required.',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Invalid email address.',
                      },
                    }}
                    render={({ field: { onChange } }) => (
                      <Input
                        value={userInfo.emailAddress}
                        dataTestId="emailAddress"
                        onInputChange={(e) => {
                          setUserInfo({ ...userInfo, emailAddress: e.target.value });
                          onChange(e.target.value);
                        }}
                      />
                    )}
                  />
                  {errors?.emailAddress && (
                    <div className={styles.error}>{errors.emailAddress.message}</div>
                  )}
                </div>
              </div>
            </Case>
          </Switch>
          {isProductSettingsAdmin && (
            <div className={styles.field}>
              <SettingText label={'Association'} size={'large'} />
              <div className={styles.settingReadInfo}>{selectedUser.association}</div>
            </div>
          )}
          {isPersonaBasedManagementAllowed && (
            <PersonaFragment
              currentOrganizationId={organizationId}
              userData={userData}
              userSettings={userSettings}
              setUserSettings={setUserSettings}
            />
          )}
          {isScimSettingEnabled && (
            <div className={styles.field}>
              <SettingText label={'Groups'} size={'large'} />
              <ScimFragment organizationId={organizationId} user={selectedUser} />
            </div>
          )}
          {userData.phoneNumbers &&
            userData.phoneNumbers.length > 0 &&
            userData.phoneNumbers[0].phoneNumber[0].$t && (
              <div className={styles.phoneNumberFields}>
                <SettingText label={'Phone Numbers'} size={'large'} />
                <div className={styles.phoneNumbers}>
                  {userData.phoneNumbers.map((currentNumber) => {
                    return currentNumber.phoneNumber.map((phoneNumberData, index) => {
                      return (
                        <div key={`phone-${index}`} className={styles.phoneNumber}>
                          <div className={styles.number}>{phoneNumberData.$t}</div>
                          <div
                            className={styles.deleteIcon}
                            onClick={async () => {
                              const confirm = await window.confirm(
                                'Are you sure you want to delete this phone?'
                              );
                              if (confirm) {
                                setUnlinkPhoneNumber(phoneNumberData.$t);
                              }
                            }}
                          >
                            <XIcon />
                          </div>
                        </div>
                      );
                    });
                  })}
                </div>
              </div>
            )}
          {selectedUser?.pagers && selectedUser?.pagers.length > 0 && (
            <div className={styles.field}>
              <SettingText label={'Pager Number'} size={'large'} alignment={'top'} />
              <div className={styles.settingReadInfo}>
                {selectedUser?.pagers.map((currentPager) => {
                  return (
                    <div className={styles.pagerNumber} key={currentPager}>
                      {currentPager}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {isLocationsEnabled && (
            <div className={styles.field}>
              <SettingText label={'Locations'} size={'large'} />
              <div className={styles.settingReadInfo}>
                <LocationsList locations={userData.locations} formTopRef={formTopRef} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ScimFragment({ organizationId, user }: { organizationId: string; user: User }) {
  const [isLoading, setIsLoading] = useState(false);
  const [groupInfo, setGroupInfo] = useState<string>('No Groups');

  useEffect(() => {
    setIsLoading(true);
    TCClient.scim
      .findScimUser(organizationId, user.id)
      .then((scimUser: ScimUser) => {
        setGroupInfo(scimUser?.groups.map((group) => group.display).join(', ') || 'No Groups');
      })
      .catch(() => setGroupInfo('An error occurred while fetching groups'))
      .finally(() => setIsLoading(false));
  }, [organizationId, user.id]);

  return (
    <div className={styles.settingReadInfo}>
      {isLoading ? (
        <DotsIndicator color="var(--neutral-1000)" size={12} marginTop={30} marginRight={5} />
      ) : (
        groupInfo
      )}
    </div>
  );
}

const getUserPersonas = (userData: UserData, currentOrganizationId: string) => {
  const personas = userData.organizationSettings?.find(
    (org) => org.organizationId === currentOrganizationId
  )?.permission_groups;

  if (Array.isArray(personas)) {
    return personas
      .filter((group) => group.persona_name?.length)
      .map((persona) => persona.persona_name)
      .join(', ');
  }

  return personas?.persona_name?.length ? personas?.persona_name : undefined;
};

function PersonaFragment({
  setUserSettings,
  userSettings,
  userData,
  currentOrganizationId,
}: {
  setUserSettings: (us: UserSettings) => void;
  userData: UserData;
  currentOrganizationId: string;
  userSettings?: UserSettings;
}) {
  const userPersonas = useMemo(() => {
    const personas = getUserPersonas(userData, currentOrganizationId);
    return personas ? personas : 'No Personas Assigned';
  }, [userData, currentOrganizationId]);
  return userSettings ? (
    <>
      <div className={styles.field}>
        <SettingText label={'Personas'} size={'large'} />
        <div className={styles.settingReadInfo}>{userPersonas}</div>
      </div>
      <div className={styles.field}>
        <SettingText label={'Exclude'} size={'large'}>
          <div className={styles.tooltip}>
            ?
            <span className={styles.tooltipText}>
              When a user is excluded from Personas, their user permissions MUST be managed
              individually
            </span>
          </div>
        </SettingText>
        <div className={styles.checkBoxContainer}>
          <Checkbox
            checked={userSettings?.personaMappingExclusion}
            onClick={() => {
              setUserSettings({
                ...userSettings,
                personaMappingExclusion: !userSettings.personaMappingExclusion,
              });
            }}
          />
        </div>
      </div>
    </>
  ) : null;
}
